// Phase 10 — TemplatesPage.
//
// Signal, chain, and report template management. Shows all
// 12 signal types, 6 chain templates, and 5 report templates
// in a tabbed grid layout with premium design tokens.

import { useState, useMemo } from "react";
import type { CSSProperties } from "react";
import {
  useSignalTemplates,
  useChainTemplates,
  useReportTemplates,
} from "../../templates/useTemplates.ts";
import type {
  SignalTemplate,
  ChainTemplate,
  ReportTemplate,
} from "../../templates/useTemplates.ts";
import { cardLow, cardMedium } from "../../styles/elevation.ts";
import { transitionStandard, transitionFast } from "../../styles/motion.ts";

// ─── Styles ───────────────────────────────────────────────────

const root: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

const header: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

const title: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.4rem, 3vw, 2rem)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

const subtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  marginTop: "4px",
};

const countLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
};

const tabBar: CSSProperties = {
  display: "flex",
  gap: "2px",
  borderBottom: "1px solid var(--sz-border-subtle)",
};

const tabBase: CSSProperties = {
  ...transitionFast,
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "var(--sz-space-2) var(--sz-space-3)",
  background: "transparent",
  borderTop: "none",
  borderRight: "none",
  borderLeft: "none",
  borderBottom: "2px solid transparent",
  color: "var(--sz-text-muted)",
  cursor: "pointer",
};

const tabActive: CSSProperties = {
  ...tabBase,
  color: "var(--sz-text-primary)",
  borderBottom: "2px solid var(--sz-amber)",
};

const grid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
  gap: "var(--sz-space-3)",
};

const card: CSSProperties = {
  ...cardMedium,
  ...transitionStandard,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  borderTop: "3px solid var(--sz-amber)",
};

const cardInactive: CSSProperties = {
  ...card,
  borderTop: "3px solid var(--sz-border-subtle)",
  opacity: 0.6,
};

const cardTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.1rem",
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1.2,
};

const cardType: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-amber)",
};

const cardDesc: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.5,
};

const metaRow: CSSProperties = {
  display: "flex",
  gap: "var(--sz-space-2)",
  flexWrap: "wrap",
  alignItems: "center",
};

const metaPill: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-recessed)",
  color: "var(--sz-text-muted)",
};

const metaPillGreen: CSSProperties = {
  ...metaPill,
  background: "rgba(34, 197, 94, 0.12)",
  color: "var(--sz-success-text)",
};

const metaPillAmber: CSSProperties = {
  ...metaPill,
  background: "rgba(245, 158, 11, 0.12)",
  color: "var(--sz-amber)",
};

const emptyState: CSSProperties = {
  ...cardLow,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-6) var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
};

const emptyIcon: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "2rem",
  color: "var(--sz-text-muted)",
  opacity: 0.5,
};

const emptyTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.1rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-secondary)",
};

const emptyNote: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-text-muted)",
  textAlign: "center",
  maxWidth: "360px",
};

const phantomBadge: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  opacity: 0.5,
  textAlign: "center",
  padding: "var(--sz-space-2) 0",
};

// ─── Tab type ─────────────────────────────────────────────────

type TabKey = "signals" | "chains" | "reports";

// ─── Signal Card ──────────────────────────────────────────────

function SignalCard({ t }: { t: SignalTemplate }): JSX.Element {
  return (
    <div style={t.active ? card : cardInactive}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={cardType}>{t.signal_type.replace(/_/g, " ")}</span>
        <span style={metaPill}>{t.market_type}</span>
      </div>
      <span style={cardTitle}>{t.label}</span>
      {t.description ? <span style={cardDesc}>{t.description}</span> : null}
      <div style={metaRow}>
        <span style={metaPill}>Priority: {t.default_priority}</span>
        <span style={metaPill}>Min conf: {(t.confidence_min * 100).toFixed(0)}%</span>
        {t.auto_approve ? <span style={metaPillGreen}>Auto-approve</span> : null}
        {t.required_fields.length > 0 ? (
          <span style={metaPillAmber}>{String(t.required_fields.length)} req fields</span>
        ) : null}
      </div>
    </div>
  );
}

// ─── Chain Card ───────────────────────────────────────────────

function ChainCard({ t }: { t: ChainTemplate }): JSX.Element {
  return (
    <div style={t.active ? card : cardInactive}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={cardType}>{t.chain_type.replace(/_/g, " ")}</span>
        <span style={metaPill}>Risk: {t.risk_profile}</span>
      </div>
      <span style={cardTitle}>{t.label}</span>
      {t.description ? <span style={cardDesc}>{t.description}</span> : null}
      <div style={metaRow}>
        <span style={metaPill}>Max legs: {String(t.max_legs)}</span>
        {t.strategy ? <span style={metaPillAmber}>{t.strategy}</span> : null}
        {t.market_types.map((m) => (
          <span key={m} style={metaPill}>{m}</span>
        ))}
      </div>
    </div>
  );
}

// ─── Report Card ──────────────────────────────────────────────

function ReportCard({ t }: { t: ReportTemplate }): JSX.Element {
  return (
    <div style={t.active ? card : cardInactive}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={cardType}>{t.report_type.replace(/_/g, " ")}</span>
        <span style={metaPill}>{t.frequency.replace(/_/g, " ")}</span>
      </div>
      <span style={cardTitle}>{t.label}</span>
      {t.description ? <span style={cardDesc}>{t.description}</span> : null}
      <div style={metaRow}>
        <span style={metaPill}>{String(t.sections.length)} sections</span>
        {t.auto_generate ? <span style={metaPillGreen}>Auto-generate</span> : null}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export function TemplatesPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<TabKey>("signals");

  const signals = useSignalTemplates();
  const chains = useChainTemplates();
  const reports = useReportTemplates();

  const totalCount = signals.templates.length + chains.templates.length + reports.templates.length;

  return (
    <div style={root}>
      {/* Header */}
      <div style={header}>
        <div>
          <h2 style={title}>Templates</h2>
          <div style={subtitle}>
            12 signal types · 6 chain templates · 5 report templates
          </div>
        </div>
        <span style={countLabel}>{String(totalCount)} templates</span>
      </div>

      {/* Tabs */}
      <div style={tabBar}>
        <button
          type="button"
          style={activeTab === "signals" ? tabActive : tabBase}
          onClick={() => setActiveTab("signals")}
        >
          Signals ({String(signals.templates.length)})
        </button>
        <button
          type="button"
          style={activeTab === "chains" ? tabActive : tabBase}
          onClick={() => setActiveTab("chains")}
        >
          Chains ({String(chains.templates.length)})
        </button>
        <button
          type="button"
          style={activeTab === "reports" ? tabActive : tabBase}
          onClick={() => setActiveTab("reports")}
        >
          Reports ({String(reports.templates.length)})
        </button>
      </div>

      {/* Content */}
      {activeTab === "signals" ? (
        signals.templates.length === 0 ? (
          <div style={emptyState}>
            <span style={emptyIcon}>◆</span>
            <span style={emptyTitle}>No Signal Templates</span>
            <span style={emptyNote}>
              Signal templates define the schema for each of the 12 signal types
              across Football, Polymarket, Forex, and Crypto markets.
            </span>
          </div>
        ) : (
          <div style={grid}>
            {signals.templates.map((t) => (
              <SignalCard key={t.id} t={t} />
            ))}
          </div>
        )
      ) : activeTab === "chains" ? (
        chains.templates.length === 0 ? (
          <div style={emptyState}>
            <span style={emptyIcon}>⟐</span>
            <span style={emptyTitle}>No Chain Templates</span>
            <span style={emptyNote}>
              Chain templates define multi-leg strategies — ROLLOVER,
              POWERBALL, LADDER, and custom cross-market chains.
            </span>
          </div>
        ) : (
          <div style={grid}>
            {chains.templates.map((t) => (
              <ChainCard key={t.id} t={t} />
            ))}
          </div>
        )
      ) : reports.templates.length === 0 ? (
        <div style={emptyState}>
          <span style={emptyIcon}>◈</span>
          <span style={emptyTitle}>No Report Templates</span>
          <span style={emptyNote}>
            Report templates define structured output formats — daily briefs,
            weekly summaries, performance reviews, risk audits, and compliance reports.
          </span>
        </div>
      ) : (
        <div style={grid}>
          {reports.templates.map((t) => (
            <ReportCard key={t.id} t={t} />
          ))}
        </div>
      )}

      <span style={phantomBadge}>Phantom · simulated</span>
    </div>
  );
}
